import { useEffect, useState } from "react";
import { Text } from "@primer/react";
import { CheckCircleFillIcon, ChecklistIcon, ChevronRightIcon, CircleIcon, LockIcon, ProjectIcon, ShieldCheckIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceResultItem } from "../components/ComplianceResultItem/ComplianceResultItem.jsx";
import { ComplianceMetric } from "../components/ComplianceMetric/ComplianceMetric.jsx";
import { ExpansionSection } from "../components/ExpansionSection/ExpansionSection.jsx";
import { formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { PolicyIdentity, policyHref } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { PolicyFinding } from "../components/PolicyFinding/PolicyFinding.jsx";
import { PropertyValueIdentity, propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { SampleProfileAction } from "../components/SampleProfileAction/SampleProfileAction.jsx";
import { complianceGap, evaluateCompliance } from "../domain/compliance.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { hasStartedOnboarding, isPristineProfile, markOnboardingStarted } from "../storage/onboardingPreferences.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import { usePresentationState } from "../storage/presentationState.js";
import "./CompliancePage.css";

const statusGroups = [
  { key: "violation", label: "Violations", singular: "Violation" },
  { key: "warning", label: "Advisories", singular: "Advisory" },
  { key: "compliant", label: "Compliant", singular: "Compliant" },
  { key: "not-evaluated", label: "Not evaluated", singular: "Not evaluated" }
];

export function CompliancePage({ policyId, profileId: profileIdOverride, repository }) {
  const application = useApplicationDocument(repository);
  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <ComplianceLoading />;

  return <ComplianceContent application={application} policyId={policyId} presentationProfileId={application.profile?.id ?? profileIdOverride} profileId={application.profile?.id} />;
}

function ComplianceContent({ application, policyId, presentationProfileId, profileId }) {
  const { document } = application;
  const [expansion, setExpansion] = usePresentationState({
    defaultValue: {}, lifetime: "session", name: "expansion:status", page: "compliance", profileId: presentationProfileId, scope: "profile", validate: booleanMap
  });
  const pristine = isPristineProfile(document);
  const [onboardingStarted, setOnboardingStarted] = useState(() => hasStartedOnboarding(profileId));

  const evaluations = evaluateCompliance(document);
  const gap = complianceGap(evaluations);
  const routedPolicyId = evaluations.some((evaluation) => evaluation.policy.id === policyId) ? policyId : null;
  const findings = evaluations.flatMap((evaluation) => evaluation.results.length
    ? evaluation.results.map((result) => ({ evaluation, result, status: findingStatus(result.status) }))
    : [{ evaluation, result: null, status: "not-evaluated" }]);
  const groups = statusGroups.map((group) => ({ ...group, findings: findings
    .filter((finding) => finding.status === group.key)
    .sort(compareFindingPriority) }))
    .filter((group) => group.key !== "not-evaluated" || group.findings.length);
  const setupSteps = profileId ? portfolioSetupSteps(document) : [];
  const setupComplete = setupSteps.length > 0 && setupSteps.every((step) => step.complete);

  useEffect(() => {
    if (routedPolicyId) navigateToHash(policyHref(routedPolicyId));
  }, [routedPolicyId]);

  function beginPersonalSetup() {
    markOnboardingStarted(profileId);
    setOnboardingStarted(true);
  }

  return <article className="compliance-page">
    <PageHeading description={evaluations.length ? <span className="compliance-introduction"><ComplianceMetric advisory={Math.round(gap.advisory / 100)} kind="gap" mandatory={Math.round(gap.mandatory / 100)} size="large" /><span>Review how the current portfolio performs against its policies.</span></span> : "Review how the current portfolio performs against its policies."}>Compliance</PageHeading>
    {profileId && pristine && !onboardingStarted ? <Welcome application={application} onPersonalSetup={beginPersonalSetup} /> : profileId && !setupComplete && !routedPolicyId ? <SetupGuide application={application} steps={setupSteps} /> : <>
      <AllocationWarning document={document} />
      {evaluations.length ? <div className="compliance-groups by-status">{groups.map((group) => <FindingGroup document={document} expansion={expansion} findings={group.findings} group={group} key={group.key} onExpansionChange={setExpansion} />)}</div> : <ComplianceEmpty />}
    </>}
  </article>;
}

function Welcome({ application, onPersonalSetup }) {
  const highlights = [
    { icon: ProjectIcon, title: "View from every angle", description: "Organize the same portfolio through flexible Browser views." },
    { icon: ChecklistIcon, title: "Set your own rules", description: "Define policies and track violations, advisories, and compliance." },
    { icon: LockIcon, title: "Private by design", description: "No login. Your data stays in this browser, with optional backups." }
  ];
  return <section className="onboarding-welcome" aria-labelledby="welcome-title">
    <span className="welcome-icon"><ShieldCheckIcon size="medium" aria-hidden="true" /></span>
    <div className="welcome-copy">
      <h2 id="welcome-title">Be your own regulator</h2>
      <p>Organize your portfolio, define the rules that matter to you, and see how your allocation complies.</p>
      <ul className="welcome-highlights">{highlights.map((highlight) => {
        const Icon = highlight.icon;
        return <li key={highlight.title}><Icon aria-hidden="true" /><span><strong>{highlight.title}</strong>{highlight.description}</span></li>;
      })}</ul>
    </div>
    <div className="welcome-actions">
      <SampleProfileAction application={application}>Explore a sample portfolio</SampleProfileAction>
      <Button tone="secondary" onClick={onPersonalSetup}>Set up my own portfolio</Button>
      <Button as="a" tone="quiet" href="#/help">Read the help guide</Button>
    </div>
  </section>;
}

function SetupGuide({ application, steps }) {
  const completed = steps.filter((step) => step.complete).length;
  return <section className="setup-guide" aria-labelledby="setup-title">
    <header>
      <div><h2 id="setup-title">Set up your portfolio</h2><p>Work through these areas in any order. Your progress is saved automatically.</p></div>
      <Text>{completed} of {steps.length} complete</Text>
    </header>
    <div className="setup-sample-option">
      <div><strong>Want to explore a working portfolio?</strong><p>Load complete sample data in a separate profile.</p></div>
      <SampleProfileAction application={application}>Load sample data</SampleProfileAction>
    </div>
    <Text className="setup-continuation">or continue setting up this portfolio</Text>
    <ol>{steps.map((step) => {
      const Icon = step.complete ? CheckCircleFillIcon : CircleIcon;
      return <li className={step.complete ? "complete" : ""} key={step.href}>
        <Icon aria-hidden="true" />
        <div><a href={step.href}>{step.title}</a><p>{step.description}</p></div>
      </li>;
    })}</ol>
  </section>;
}

function portfolioSetupSteps(document) {
  const allocationTotal = (document.allocations ?? []).reduce((total, allocation) => total + allocation.basisPoints, 0);
  return [
    { href: "#/properties", title: "Define portfolio properties", description: "Create the classifications used by assets, Browser views, and policies.", complete: document.properties.length > 0 },
    { href: "#/assets", title: "Add and classify assets", description: "Add the holdings that make up this portfolio.", complete: document.assets.length > 0 },
    { href: "#/allocation", title: "Record the current allocation", description: "Enter a complete allocation totaling 100%.", complete: document.allocations.length > 0 && allocationTotal === 10000 },
    { href: "#/policies", title: "Add compliance policies", description: "Define required limits and preferred ranges.", complete: (document.policies ?? []).length > 0 }
  ];
}

function FindingGroup({ document, expansion, findings, group, onExpansionChange }) {
  const expansionKey = `group:${group.key}`;
  const expanded = expansion[expansionKey] ?? ["violation", "warning"].includes(group.key);
  function toggleExpanded() {
    onExpansionChange((current) => ({ ...current, [expansionKey]: !expanded }));
  }
  const label = findings.length === 1 ? group.singular : group.label;
  return <ExpansionSection className={`finding-group ${group.key}`} expanded={expanded}>
    <button className="finding-group-summary" type="button" aria-expanded={expanded} onClick={toggleExpanded}>
      <ChevronRightIcon aria-hidden="true" />
      <strong>{findings.length} {label}</strong>
    </button>
    {expanded ? <div className="finding-list">{findings.length
      ? findings.map((finding) => <Finding document={document} finding={finding} key={`${finding.evaluation.policy.id}-${finding.result?.key ?? "empty"}`} />)
      : <Text className="finding-empty">No {group.label.toLocaleLowerCase()}.</Text>}</div> : null}
  </ExpansionSection>;
}

function Finding({ document, finding }) {
  const { policy } = finding.evaluation;
  const { value } = usePortfolioValue();
  if (!finding.result) return <ComplianceResultItem
    className="compliance-page-result unavailable"
    detail={<Text className="compliance-unavailable-message">There is nothing to evaluate for this policy.</Text>}
    href={policyHref(policy.id)}
    status="not-evaluated"
    summary={<PolicyIdentity document={document} policy={policy} variant="subject" />}
  />;
  const { subject } = finding.result;
  const subjectPresentation = findingSubject(document, subject);
  const amount = value !== null ? formatPortfolioAmount(amountForBasisPoints(value, finding.result.basisPoints)) : null;
  const allocation = <Text className="compliance-finding-allocation">({formatPercentage(finding.result.basisPoints)}%{amount ? <span className="portfolio-amount"> · {amount}</span> : null})</Text>;
  const policyMeta = policy.scope.type === "specific" ? null : <span className="compliance-policy-meta"><PolicyIdentity document={document} policy={policy} size="small" variant="subject" /></span>;
  const summary = <div className="compliance-page-summary"><span className="compliance-finding-subject">{subjectPresentation.identity}{allocation}</span>{policyMeta}</div>;
  const detail = <PolicyFinding document={document} policy={policy} result={finding.result} showIdentity={false} />;
  return <ComplianceResultItem className="compliance-page-result" detail={detail} href={subjectPresentation.href} status={finding.result.status} summary={summary} />;
}

function booleanMap(value) {
  if (!value || Array.isArray(value) || typeof value !== "object") return null;
  return Object.fromEntries(Object.entries(value).filter(([, expanded]) => typeof expanded === "boolean"));
}

function findingSubject(document, subject) {
  if (subject.type === "asset") return { href: assetHref(subject.asset.id), identity: <AssetIdentity asset={subject.asset} copyableTicker={false} variant="sentence" /> };
  const property = document.properties.find((item) => item.id === subject.propertyId);
  const value = property.values.find((item) => item.id === subject.valueId);
  return { href: propertyValueHref(property.id, value.id), identity: <PropertyValueIdentity property={property} value={value} variant="sentence" /> };
}

function findingStatus(status) {
  if (status === "non-compliant") return "violation";
  if (status === "warning") return "warning";
  return "compliant";
}

function compareFindingPriority(left, right) {
  return findingDelta(right) - findingDelta(left)
    || (right.result?.basisPoints ?? 0) - (left.result?.basisPoints ?? 0);
}

function findingDelta(finding) {
  if (!finding.result) return 0;
  const bounds = finding.status === "violation"
    ? finding.evaluation.policy.must
    : finding.status === "warning" ? finding.evaluation.policy.should : null;
  if (!bounds) return 0;
  const minimum = bounds.minimum ?? 0;
  const maximum = bounds.maximum ?? 10000;
  return Math.max(minimum - finding.result.basisPoints, finding.result.basisPoints - maximum, 0);
}

function AllocationWarning({ document }) {
  if (document.assets.length === 0) return null;
  const total = document.assets.reduce((sum, asset) => sum + ((document.allocations ?? []).find((allocation) => allocation.assetId === asset.id)?.basisPoints ?? 0), 0);
  if (total === 10000) return null;
  const difference = Math.abs(10000 - total);
  return <Alert tone="warning" heading={`Portfolio allocation totals ${formatPercentage(total)}%`}>{formatPercentage(difference)}% is {total < 10000 ? "unallocated" : "overallocated"}. <a className="compliance-allocation-link" href="#/allocation">Edit allocation</a></Alert>;
}

function ComplianceEmpty() {
  return <div className="compliance-empty"><span><ShieldCheckIcon size="medium" /></span><h2>No policies to evaluate</h2><p>Add policies to start checking this portfolio.</p><Button as="a" href="#/policies">Go to Policies</Button></div>;
}

function ComplianceLoading() { return <article className="compliance-page"><PageHeading description="Loading portfolio compliance…">Compliance</PageHeading></article>; }
